/**
 * print-fallback-modal.component.ts
 *
 * app-print-fallback-modal
 *
 * The Printing screen: where this location's printing goes, and which of the
 * four printing arrangements is in force right now.
 *
 * TWO DIFFERENT FACTS, and conflating them is what this screen used to get
 * wrong. Where a location's printing goes is CONFIGURATION — Kennewick has no
 * Zebra label printer yet, so its labels come off a Dymo at a desktop computer,
 * and that is not an outage, not a choice, and not something to warn anyone
 * about. Whether a printer BROKE is a temporary
 * override, set by the person standing at it. The old screen expressed the first
 * as a locked selection of the second, so a normally-configured facility
 * permanently displayed "print system is down" and could not select anything else.
 *
 * Hence the framing here. The screen is not working / partly broken / broken —
 * it is TWO ROWS, ONE PER DOCUMENT, each a two-way choice:
 *
 *   Labels          sent automatically | printed from a desktop computer only
 *   Copier sheets   sent automatically | printed from a desktop computer only
 *
 * One row per document because that is the operator's actual event: the Zebra is
 * jammed, or the copier is out — never both at once, and never a state of the
 * whole print system. The four stored modes are exactly the four combinations of
 * these two rows, and {@link modeFor} is the translator; nothing here needs to
 * name a mode at all.
 *
 * "DESKTOP COMPUTER ONLY", not "printed by hand" and not "browser": a desktop can
 * print automatically too, so what the second choice actually removes is the
 * ability to print from anywhere else. The operator does not care that the
 * mechanism is a browser — they care that somebody has to go stand at a desktop.
 *
 * The axis is AUTOMATIC vs DESKTOP-ONLY, per document, not one destination versus
 * another. A label falling to desktop-only moves to a different machine (a Dymo at
 * a desktop instead of the Zebra); a copier sheet falling to "by hand" does not
 * move — the building still has one copier, and what changes is whether the
 * print API sends the job there or an operator picks that same copier out of a
 * print dialog (or, on the browser path, ANY copier on the network — a free
 * choice the print API does not offer).
 *
 * A facility's printers decide which of those is its BASELINE (see
 * {@link baselineMode}); an operator can drop to a more manual one when hardware
 * dies, and cannot climb to one their building cannot support. Selecting the
 * baseline back means "nothing is wrong", stored as no override at all — which is
 * what keeps a permanent arrangement from raising an outage banner.
 *
 * Deliberately NOT in the per-job print config sheet (the desktop form's print
 * modal, the mobile Review step's labels sheet). "This printer is broken" is a
 * fact about the building, not a property of this delivery — putting it beside
 * the copy counts would ask every operator to re-decide it on every log, and
 * would leave it set on exactly one log when they did.
 *
 * Two rows over one stored field: {@link PrintFallbackMode} stays a single value,
 * because the four modes are exactly the four combinations of the two rows —
 * see {@link labelAutomatic}, {@link sheetAutomatic}, {@link modeFor}. The modes
 * are NOT a single ordered scale: `zebraDown` and `copierDown` are independent
 * failures, neither implying the other. Only `printApiDown` subsumes both, which
 * is why the rows are independent controls rather than one slider.
 *
 * Driven like the app's other configure-and-act modals ({@link
 * ReprintLabelsModalComponent}, {@link PwaInstructionsModalComponent}): the
 * parent owns `isOpen` and closes on `closeModal`. Uses the global `.card-popup`
 * shell from src/styles.css so it matches them.
 */

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { PrintFallbackService } from '../../../service/print-fallback.service';
import { AssetsDataService } from '../../../service/assets-data.service';
import { SelectedLocationService } from '../../../service/selected-location.service';
import { ScreenSizeService } from '../../../service/screen-size.service';
import { Location } from '../../../models/assets-data/LocationConfig.interface';
import {
  PrintCapability,
  PrintDoc,
  PrintFallbackMode,
  locationCapability,
  labelAutomatic,
  sheetAutomatic,
  modeFor,
} from '../../../domain/receiving-log/rules/print-path.rules';

/** What one row's radio group binds to. Not a {@link PrintFallbackMode}: a row
 *  describes ONE document, and the mode is the pair of them. */
type DocChoice = 'auto' | 'desktop';

/**
 * The body of one row's info button, in the two parts an operator reads in
 * order: what each choice does, then which situation calls for the second one.
 *
 * Held as data rather than written into the template because both rows have the
 * same shape and both name this facility's own printers.
 */
interface DocInfo {
  /** What "Sent automatically" does for this document. */
  automatic: string;
  /** What "Printed from a desktop computer only" does for this document. */
  desktopOnly: string;
  /** The situation that calls for the desktop-only choice. */
  whenToUse: string;
  /** That the OTHER document is untouched — the thing two rows must say. */
  otherDoc: string;
}

/**
 * One document's row: which choice is in force, why the automatic side may be
 * unselectable here, and the info-button body.
 *
 * One row per document rather than one option per mode. The operator's event is
 * about a single printer, so the screen asks about a single printer; the pair of
 * answers becomes a mode through {@link modeFor} and never before.
 */
interface DocRow {
  doc: PrintDoc;
  /** The document, in the fixed vocabulary — "Labels", "Copier sheets". */
  title: string;
  /** Which choice is in force right now, from `effectiveMode`. */
  selected: DocChoice;
  /**
   * Set when this facility has no printer for this document, so "Sent
   * automatically" cannot be chosen. Shown beside the disabled control, never
   * behind the info button — a greyed-out control whose reason is hidden is a
   * dead control.
   */
  unavailableReason?: string;
  info: DocInfo;
}

@Component({
  selector: 'app-print-fallback-modal',
  standalone: true,
  imports: [CommonModule, IonicModule],
  templateUrl: './print-fallback-modal.component.html',
  styleUrls: ['./print-fallback-modal.component.css'],
})
export class PrintFallbackModalComponent implements OnInit, OnDestroy {
  private readonly _printFallback = inject(PrintFallbackService);
  private readonly _assetsData = inject(AssetsDataService);
  private readonly _selectedLocation = inject(SelectedLocationService);
  private readonly _screenSize = inject(ScreenSizeService);

  private _modeSubscription?: Subscription;
  private _locationSubscription?: Subscription;

  /** Whether the modal is presented. Owned by the parent. */
  @Input() isOpen = false;

  /** Emitted when the user closes the modal (button or backdrop dismiss). */
  @Output() closeModal = new EventEmitter<void>();

  /** The currently stored mode, bound to the radio group. */
  public mode: PrintFallbackMode = 'normal';

  /** The location whose configuration this screen describes. */
  public location?: Location;

  /**
   * The two document rows.
   *
   * A field rebuilt by {@link _refresh}, not a getter — see its doc for why that
   * distinction was load-bearing enough to freeze the app.
   */
  public rows: DocRow[] = [];

  /**
   * Which row's info button is open, or null. One modal serves both rows.
   *
   * Held as the DOCUMENT rather than the row object: `rows` is rebuilt whenever
   * the mode or the location changes, so a held row would go stale behind an
   * open info modal and keep describing the previous location's printers.
   */
  public openInfoDoc: PrintDoc | null = null;

  public ngOnInit(): void {
    this._modeSubscription = this._printFallback.mode$.subscribe((mode) => {
      this.mode = mode;
      this._refresh();
    });
    // Subscribed rather than read on open: the location picker sits in the same
    // shell as the menu row that opens this, so it can change underneath it.
    this._locationSubscription = this._selectedLocation.selectedLocation$.subscribe((selected) => {
      this.location = this._assetsData.locations?.find((l) => l.locationLabel === selected?.label);
      this._refresh();
    });
  }

  /**
   * Rebuilds the two list-valued view models, and ONLY when their inputs change.
   *
   * These were getters, and that was the other half of the freeze. A getter that
   * returns a fresh array hands `*ngFor` a new identity on every change-detection
   * pass, so Angular destroyed and recreated all four `ion-radio` elements every
   * single check. Recreating the radios makes `ion-radio-group` re-sync and emit
   * `ionChange`, which lands in {@link onSelect}, which wrote to the service,
   * which re-rendered — round and round until the tab stopped responding.
   *
   * Their inputs are exactly two: the stored mode and the selected location. Both
   * arrive through the subscriptions above, so recomputing there is complete.
   */
  private _refresh(): void {
    this.rows = this._buildRows();
  }

  public ngOnDestroy(): void {
    this._modeSubscription?.unsubscribe();
    this._locationSubscription?.unsubscribe();
  }

  /** This facility's name, for the line under the title and for each reason. */
  public get locationLabel(): string {
    return this.location?.locationLabel ?? 'this location';
  }

  /** Which printers the print API can reach here. */
  public get capability(): PrintCapability {
    return locationCapability(this.location);
  }

  /**
   * The note telling a phone user that a document is not going to print here,
   * naming exactly which one — or empty when nothing is waiting.
   *
   * NEVER behind the info button. This is the sentence that stops someone
   * standing at a phone waiting for paper that is not coming: the browser's
   * print dialog on a phone reaches no warehouse printer, so a document on the
   * desktop-only side is a document somebody has to go to a desktop to print.
   *
   * Combined rather than one note per row when both documents are waiting: two
   * sentences differing in one word read as a rendering bug, not as emphasis.
   * A plain string, not a list, so it can be a getter without handing `*ngFor`
   * a new array (see {@link _refresh}).
   */
  public get phoneWaitingNote(): string {
    if (!this.isMobile) { return ''; }
    const effective = this.effectiveMode;
    const labelWaits = !labelAutomatic(effective);
    const sheetWaits = !sheetAutomatic(effective);
    if (!labelWaits && !sheetWaits) { return ''; }
    const docs = labelWaits && sheetWaits
      ? 'labels and copier sheets'
      : labelWaits ? 'labels' : 'copier sheets';
    // States the action rather than the predicament: "so they wait" describes
    // the documents' situation, which is not something the operator can act on.
    // What they can act on is the reprint, so the note ends on that.
    return `${docs.charAt(0).toUpperCase()}${docs.slice(1)} can only be printed from a desktop `
      + `computer, and this is a phone. After submitting a log that includes ${docs}, open the `
      + 'log table on a desktop computer and reprint them there.';
  }

  /** Whether this is a phone, where a browser print dialog reaches no printer. */
  public get isMobile(): boolean {
    return this._screenSize.isMobile.getValue();
  }

  /**
   * The state this facility's own configuration puts it in, with no override on.
   *
   * This is the half that was missing. The four states are not "working /
   * partly broken / broken" — they are four descriptions of WHERE PRINTING
   * GOES, and a facility whose Zebra has not been installed yet lives
   * permanently in `copierDown` (or `printApiDown`, with no copier either).
   * Nothing is wrong there; that is simply how Kennewick prints until its
   * printer arrives. Presenting that as a fault asked those operators to read
   * their normal day as an outage.
   *
   * Derived from {@link modeFor}, not a hand-written if/else: a Zebra but no
   * copier used to be undescribable by the three-state model and fell back to
   * `normal`, which was wrong. It is now `copierDown` — the state that means
   * "labels automatic, sheets not" — closing that gap. No location is configured
   * that way today, and each card answers for its own document either way.
   */
  public get baselineMode(): PrintFallbackMode {
    const caps = this.capability;
    return modeFor(caps.hasZebraPrinter, caps.hasCopierPrinter);
  }

  /**
   * What is actually happening right now: the facility's baseline, degraded
   * further by whichever of the operator's override and the baseline itself is
   * less automatic — per document.
   *
   * Not the raw stored mode. At Kennewick the stored mode is `normal` — no one
   * has reported anything down — but its printing all comes off the computer,
   * and a screen showing "automatic printing to the label printer" selected
   * would be describing a printer that is not there. Total over all four modes,
   * with no ordering assumption: this is exactly the pair of per-document facts
   * that both the baseline and the stored mode agree are still automatic.
   */
  public get effectiveMode(): PrintFallbackMode {
    const baseline = this.baselineMode;
    return modeFor(
      labelAutomatic(baseline) && labelAutomatic(this.mode),
      sheetAutomatic(baseline) && sheetAutomatic(this.mode),
    );
  }

  /**
   * The two rows: one per document, each showing which choice is in force.
   *
   * A row's automatic side is unavailable, with the reason beside it, exactly
   * when this facility has no printer for that document — you cannot route
   * labels to a label printer that has not been installed. That is the
   * per-document form of the old whole-mode test, and it is simpler for being
   * per-document: one document, one question, no comparing a mode against a
   * baseline.
   *
   * A row is read from `effectiveMode`, not the stored mode, for the same reason
   * the radio group was: at a facility with no Zebra the stored mode is "no
   * override", but its labels come off a Dymo, and a row reading "sent
   * automatically" would describe a printer that is not there.
   *
   * WHAT THE INFO BUTTONS MAY AND MAY NOT HOLD. Two things stay out of them and
   * are visible on the screen itself: the reason a choice is unavailable (see
   * {@link DocRow.unavailableReason}) and the phone caveat (see
   * {@link phoneWaitingNote}). What goes in is the detail an operator wants only
   * when deciding: which machine, how the print actually happens, and what the
   * choice is good for.
   */
  private _buildRows(): DocRow[] {
    const caps = this.capability;
    const effective = this.effectiveMode;
    const zebra = this.location?.zebraPrinterName;
    const copier = this.location?.copierPrinterName;

    // How the paper actually appears once a document is desktop-only, and it is
    // two different routes depending on where the log was submitted. Said in
    // both info bodies rather than once in a third block on the screen: only one
    // is open at a time, so the repeat is never visible, and a standing block
    // would be clutter for the operator whose printers are all fine.
    const howItPrints =
      'Submit the log on a desktop computer and the print dialog opens by itself. Submit on a '
      + 'phone and nothing prints there — open the log table on a desktop computer and use Reprint.';

    return [
      {
        doc: 'label',
        title: 'Labels',
        selected: labelAutomatic(effective) ? 'auto' : 'desktop',
        unavailableReason: caps.hasZebraPrinter
          ? undefined
          : `${this.locationLabel} does not have a Zebra label printer set up yet, so labels `
            + 'can\'t be sent automatically.',
        info: {
          automatic: `The label goes to the Zebra label printer${zebra ? ` (${zebra})` : ''} on `
            + 'its own, from a phone or a desktop. Fewer clicks, and nothing left to do after you '
            + 'submit.',
          desktopOnly: 'The label has to go through the print dialog on a desktop, where it comes '
            + `off the Dymo attached to that computer. ${howItPrints}`,
          whenToUse: 'Choose "Printed from a desktop computer only" if the Zebra can\'t print — '
            + 'jammed, out of labels, or not answering — and you need the Dymo as a temporary '
            + 'backup.',
          otherDoc: 'Copier sheets are not affected either way.',
        },
      },
      {
        doc: 'sheet',
        title: 'Copier sheets',
        selected: sheetAutomatic(effective) ? 'auto' : 'desktop',
        unavailableReason: caps.hasCopierPrinter
          ? undefined
          : `${this.locationLabel}'s copier isn't set up for automatic printing yet.`,
        info: {
          automatic: 'The sheet goes to the copier set up for this location'
            + `${copier ? ` (${copier})` : ''} on its own, from a phone or a desktop. Fewer `
            + 'clicks, and nothing left to do after you submit.',
          // The copier's difference is a WIDER choice, not a degraded one: through
          // automatic printing there is one copier per location that the print API
          // can reach, and through the print dialog the operator picks any copier
          // that desktop can reach. Copy that reads it as "the same copier, by
          // hand" sends someone looking for the page at the wrong machine.
          desktopOnly: 'The sheet has to go through the print dialog on a desktop, where you '
            + 'choose the copier yourself. That can be any copier that computer can reach, not '
            + `only the one automatic printing uses. ${howItPrints}`,
          whenToUse: 'Choose "Printed from a desktop computer only" if you need a different '
            + 'copier in your building than the one automatic printing sends to, or if that '
            + 'copier is down.',
          otherDoc: 'Labels are not affected either way.',
        },
      },
    ];
  }

  /** The row whose help text is showing, or null. */
  public get openInfoRow(): DocRow | null {
    return this.rows.find((row) => row.doc === this.openInfoDoc) ?? null;
  }

  /**
   * Opens one row's help text in place, inside the row, and closes the other's.
   *
   * Deliberately not a second modal. A modal over a modal buries the choice the
   * help is about — the operator reads "choose this if the Zebra is jammed",
   * closes the modal to act on it, and has to find the row again. In place, the
   * radio is still under their thumb while they read.
   *
   * One open at a time so the two rows cannot both expand and push the second
   * document off a phone screen.
   */
  public onToggleInfo(row: DocRow): void {
    this.openInfoDoc = this.openInfoDoc === row.doc ? null : row.doc;
  }

  /**
   * Whether the selected state is a temporary override rather than how this
   * facility normally prints — the difference between "someone reported the
   * printer down an hour ago" and "this is Tuesday".
   */
  public get isOverride(): boolean {
    return this.mode !== 'normal';
  }

  /**
   * Applies the choice immediately rather than behind a Save button. There is one
   * setting here and it is being changed by someone standing at a broken printer;
   * a second confirmation step is one more thing to get wrong in a hurry. The
   * footer button therefore reads "Done" — it closes, it does not commit.
   *
   * SELECTING WHAT IS ALREADY SELECTED DOES NOTHING, and that guard is the point
   * rather than an optimization. `ion-radio-group` emits `ionChange` when its
   * bound value is set programmatically, not only when a person clicks — so
   * merely opening this screen arrives here once per row with what that row
   * already shows. Without the guard, rendering the screen writes to storage: at
   * Kennewick, whose baseline is not `normal`, simply looking at the Printing
   * screen called `setMode`. Displaying a setting must never change it. Two rows
   * make this MORE load-bearing than it was with one group, not less: each row
   * emits on open, and each emission that got through would write a mode built
   * from the other row's current answer.
   *
   * The pair of rows becomes a mode through {@link modeFor} and nowhere else —
   * the row that did not change contributes its current answer, read back off
   * `effectiveMode`.
   */
  public onSelectRow(doc: PrintDoc, value: string | number | undefined): void {
    if (value !== 'auto' && value !== 'desktop') { return; }
    const wantsAutomatic = value === 'auto';

    const effective = this.effectiveMode;
    const labelAuto = labelAutomatic(effective);
    const sheetAuto = sheetAutomatic(effective);

    if (wantsAutomatic === (doc === 'label' ? labelAuto : sheetAuto)) { return; }
    if (wantsAutomatic && this.rows.find((row) => row.doc === doc)?.unavailableReason) { return; }

    const next = doc === 'label'
      ? modeFor(wantsAutomatic, sheetAuto)
      : modeFor(labelAuto, wantsAutomatic);

    // Landing back on this facility's own baseline means "nothing is wrong",
    // which is stored as no override at all — not as that mode. Otherwise
    // Kennewick selecting its normal arrangement would store `printApiDown`,
    // raise the outage banner, and expire twelve hours later back into the same
    // state.
    this._printFallback.setMode(next === this.baselineMode ? 'normal' : next);
  }

  /** Whether a temporary override is in force — the only thing that gets a banner. */
  public get isActive(): boolean {
    return this._printFallback.isActive;
  }

  public onClose(): void {
    // Help closes with the screen. Left open, it is still expanded the next time
    // the screen is opened, which reads as the app's state rather than as
    // something the last person tapped.
    this.openInfoDoc = null;
    this.closeModal.emit();
  }
}
